import { IconExternalLink, IconSparkles } from "@tabler/icons-react"
import { type FC, useCallback, useEffect, useState } from "react"

import type { LandingPageLeadFormBlock } from "@forento/core/models/landing"
import { landingPageCopyCharacterLimits } from "@forento/core/schemas/landing"
import { cn } from "@forento/ui/utilities/tailwind"

import { usePlatform } from "~/contexts/UserContext"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

import EditableField from "./EditableField"
import EditorShell from "./EditorShell"
import RichEditableField from "./RichEditableField"

type Props = {
	block: LandingPageLeadFormBlock
	onChange: (block: LandingPageLeadFormBlock) => void
	onClose: () => void
}
const LeadFormBlockEditor: FC<Props> = ({ block, onChange, onClose }) => {
	const platform = usePlatform()!
	const [kind, setKind] = useState(block.content.kind)
	const [title, setTitle] = useState(block.content.title)
	const [description, setDescription] = useState(block.content.description ?? "")
	const [submitLabel, setSubmitLabel] = useState(block.content.submitLabel)

	useEffect(() => {
		const trimmedTitle = title.trim()
		onChange({
			type: "lead-form",
			content: {
				formKey: block.content.formKey,
				kind,
				title: trimmedTitle || (kind === "newsletter" ? "Subscribe to our newsletter" : "Send us a message"),
				description: description.trim() || undefined,
				submitLabel: submitLabel.trim() || (kind === "newsletter" ? "Subscribe" : "Send message"),
			},
		})
	}, [block.content.formKey, description, kind, onChange, submitLabel, title])

	const handleDescriptionChange = useCallback((value: string | null) => {
		setDescription(value ?? "")
	}, [])

	const contactConfigured = Boolean(platform.contactEmail?.trim())

	const { data: replyEmails } = query.studentEmails.listLeadForm.useQuery(undefined, {
		enabled: platform.plan.access.landingPage,
	})
	const hasReplyEmail =
		replyEmails?.some(item => item.formKey === block.content.formKey && item.email?.hasContent) ?? false
	const canUseAi = platform.enabledFeatures.includes("ai-generated-courses")

	return (
		<EditorShell onClose={onClose}>
			<section className="px-6 pt-16 pb-8 sm:pt-20 lg:px-8">
				<div className="mx-auto max-w-lg space-y-6">
					{!contactConfigured && (
						<p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-relaxed text-amber-900">
							Add a contact email in your Forento platform settings so submissions from this form are
							delivered to your inbox.
						</p>
					)}
					<div>
						<div className="mb-2 text-sm font-medium text-gray-700">Form type</div>
						<div className="flex flex-wrap gap-2">
							<button
								type="button"
								onClick={() => setKind("newsletter")}
								className={cn(
									"cursor-pointer rounded-lg border px-3 py-2 text-sm font-medium transition-colors",
									kind === "newsletter"
										? "border-indigo-600 bg-indigo-50 text-indigo-900"
										: "border-gray-200 bg-white text-gray-700 hover:border-gray-300",
								)}
							>
								Newsletter (email only)
							</button>
							<button
								type="button"
								onClick={() => setKind("inquiry")}
								className={cn(
									"cursor-pointer rounded-lg border px-3 py-2 text-sm font-medium transition-colors",
									kind === "inquiry"
										? "border-indigo-600 bg-indigo-50 text-indigo-900"
										: "border-gray-200 bg-white text-gray-700 hover:border-gray-300",
								)}
							>
								Inquiry (name + message)
							</button>
						</div>
					</div>
					<div>
						<div className="mb-2 text-sm font-medium text-gray-700">Title</div>
						<EditableField
							value={title}
							onChange={setTitle}
							placeholder="e.g. Subscribe to our newsletter"
							maxCharacters={landingPageCopyCharacterLimits.title}
							inputClassName="font-bold text-2xl text-gray-900 sm:text-3xl"
						/>
					</div>
					<div>
						<div className="mb-2 text-sm font-medium text-gray-700">Description</div>
						<RichEditableField
							initialValue={block.content.description ?? ""}
							onChange={handleDescriptionChange}
							maxCharacters={landingPageCopyCharacterLimits.body}
						/>
					</div>
					<div>
						<div className="mb-2 text-sm font-medium text-gray-700">Submit button label</div>
						<EditableField
							value={submitLabel}
							onChange={setSubmitLabel}
							placeholder={kind === "newsletter" ? "Subscribe" : "Send message"}
							maxCharacters={landingPageCopyCharacterLimits.shortLabel}
							inputClassName="text-base text-gray-900"
						/>
					</div>
					<div className="flex flex-col items-start gap-3 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3">
						<div className="flex flex-col gap-1">
							<div className="text-sm font-medium text-indigo-950">
								{hasReplyEmail ? "Automatic reply is on" : "Reply automatically"}
							</div>
							<p className="text-sm leading-relaxed text-indigo-900">
								{hasReplyEmail
									? "Everyone who submits this form receives your reply email right away."
									: kind === "newsletter"
										? "Send a welcome email the moment someone subscribes: confirm the signup, say what to expect, or deliver a freebie."
										: "Send a reply the moment someone submits: confirm you got their message and tell them what happens next."}
								{!hasReplyEmail && canUseAi && (
									<>
										{" "}
										<IconSparkles className="-mt-0.5 inline size-4" /> Forento AI can write it for
										you.
									</>
								)}
							</p>
						</div>
						<a
							href={routes.settings.studentEmail.editLeadForm(block.content.formKey)}
							target="_blank"
							rel="noreferrer noopener"
							className="inline-flex items-center gap-1 text-sm font-medium text-indigo-700 underline"
						>
							{hasReplyEmail ? "Edit reply email" : "Set up reply email"}
							<IconExternalLink className="size-3.5" />
						</a>
					</div>
				</div>
			</section>
		</EditorShell>
	)
}

export default LeadFormBlockEditor
